import { useMemo, useState, useEffect } from "react";
import {
    type ColumnDef,
    SortingState,
    flexRender,
    getCoreRowModel,
    useReactTable,
    getSortedRowModel,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/shadcn/table";
import { ArrowDown, ArrowUp, ArrowUpDown, ExternalLink } from "lucide-react";
import { Loader } from "../../common/loader";

interface DataTableProps<TData> {
    columns: ColumnDef<TData>[];
    data: TData[];
    isLoading?: boolean;
    showTier?: boolean;
    defaultSortId?: string;
    noHorizontalScroll?: boolean;
    compact?: boolean;
}

export function OverviewStatsTable<TData extends Record<string, unknown>>({
    columns,
    data,
    isLoading = false,
    defaultSortId = "totalPoints",
    noHorizontalScroll = false,
    compact = false,
}: DataTableProps<TData>) {
    const [sorting, setSorting] = useState<SortingState>([{ id: defaultSortId, desc: true }]);

    useEffect(() => {
        setSorting([{ id: defaultSortId, desc: true }]);
    }, [data, defaultSortId]);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        state: { sorting },
    });

    const getSortDirection = (header: {
        column: { getCanSort: () => boolean; getIsSorted: () => false | "asc" | "desc" };
    }) => {
        if (!header.column.getCanSort()) return null;
        return header.column.getIsSorted();
    };

    const columnRankings = useMemo(() => {
        const rankings: Record<
            string,
            { top3: number[]; highest: number | null; second: number | null; third: number | null }
        > = {};

        const currentRows = table.getRowModel().rows;

        table.getAllColumns().forEach((column) => {
            const columnValues = currentRows
                .filter((r) => {
                    const original = r.original as { playerId?: number };
                    return !original.playerId || original.playerId >= 0;
                })
                .map((r) => {
                    const cell = r.getAllCells().find((c) => c.column.id === column.id);
                    return cell?.getValue();
                })
                .filter((value): value is number => typeof value === "number")
                .sort((a, b) => b - a);

            const uniqueValues = [...new Set(columnValues)];
            rankings[column.id] = {
                top3: uniqueValues.slice(0, 3),
                highest: uniqueValues.length > 0 ? uniqueValues[0] : null,
                second: uniqueValues.length > 1 ? uniqueValues[1] : null,
                third: uniqueValues.length > 2 ? uniqueValues[2] : null,
            };
        });

        return rankings;
    }, [data, table]);

    if (isLoading) {
        return (
            <div
                className="w-full flex items-center justify-center bg-surface-container-lowest"
                style={{ minHeight: "400px" }}
            >
                <Loader message="Loading analytics data..." />
            </div>
        );
    }

    if (data.length === 0) {
        return (
            <div className="w-full h-full min-h-0 flex items-center justify-center bg-surface-container-lowest">
                <span className="text-center p-4 text-on-surface-variant">No analytics data available</span>
            </div>
        );
    }

    return (
        <div
            className={`w-full bg-surface-container-lowest ${noHorizontalScroll ? "overflow-x-hidden overflow-y-auto" : "overflow-auto"}`}
        >
            <Table className={`w-full border-collapse ${noHorizontalScroll ? "table-fixed" : "min-w-max"}`}>
                <TableHeader>
                    {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id} className="border-0">
                            {hg.headers.map((header, idx) => {
                                const sortDir = getSortDirection(header);
                                return (
                                    <TableHead
                                        key={header.id}
                                        onClick={
                                            header.column.getCanSort()
                                                ? () =>
                                                      header.column.toggleSorting(header.column.getIsSorted() === "asc")
                                                : undefined
                                        }
                                        className={`
                                            ${compact ? "px-3 py-2" : "px-6 py-4"} font-headline font-black text-[10px] uppercase tracking-widest
                                            select-none transition-colors whitespace-nowrap
                                            ${header.column.getCanSort() ? "cursor-pointer" : ""}
                                            ${idx === 0 || idx === 1 ? "sticky z-30" : "sticky z-20"}
                                            ${sortDir ? "data-table-header-sorted" : ""}
                                            data-table-header
                                        `}
                                        style={{
                                            top: 0,
                                            left: idx === 0 ? "0px" : idx === 1 ? `${compact ? 40 : 72}px` : undefined,
                                            width: header.column.columnDef.size
                                                ? `${header.column.columnDef.size}px`
                                                : undefined,
                                            minWidth: header.column.columnDef.size
                                                ? `${header.column.columnDef.size}px`
                                                : undefined,
                                        }}
                                    >
                                        {!header.isPlaceholder && (
                                            <div className="flex gap-1.5 items-center">
                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                {header.column.getCanSort() && (
                                                    <span>
                                                        {sortDir === "asc" ? (
                                                            <ArrowUp className="h-3.5 w-3.5" />
                                                        ) : sortDir === "desc" ? (
                                                            <ArrowDown className="h-3.5 w-3.5" />
                                                        ) : (
                                                            <ArrowUpDown className="h-3.5 w-3.5" />
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.map((row) => {
                        const isAverageRow =
                            row.original &&
                            (row.original as { playerId?: number }).playerId !== undefined &&
                            (row.original as { playerId?: number }).playerId! < 0;

                        return (
                            <TableRow
                                key={row.id}
                                className="data-table-row transition-colors border-0"
                                style={{
                                    background: isAverageRow ? "rgba(5,0,102,0.04)" : undefined,
                                }}
                            >
                                {row.getVisibleCells().map((cell, idx) => {
                                    const value = cell.getValue();
                                    const columnId = cell.column.id;

                                    let highlightColor: string | undefined;
                                    if (
                                        typeof value === "number" &&
                                        columnRankings[columnId] &&
                                        columnId !== "line" &&
                                        columnId !== "rank" &&
                                        columnId !== "totalPoints" &&
                                        columnId !== "matchesPlayed" &&
                                        columnId !== "period1" &&
                                        columnId !== "period2" &&
                                        columnId !== "period3"
                                    ) {
                                        const { highest, second, third } = columnRankings[columnId];
                                        if (value !== 0) {
                                            if (value === highest) highlightColor = "var(--color-medal-gold)";
                                            else if (value === second) highlightColor = "var(--color-rank-silver)";
                                            else if (value === third) highlightColor = "var(--color-rank-bronze)";
                                        }
                                    }

                                    const isSticky = idx === 0 || idx === 1;

                                    return (
                                        <TableCell
                                            key={cell.id}
                                            className={`
                                                ${compact ? "px-3 py-1.5 text-xs" : "px-6 py-3.5 text-sm"} text-on-surface whitespace-nowrap
                                                ${isSticky ? "data-table-label-cell sticky z-10" : ""}
                                            `}
                                            style={{
                                                left:
                                                    idx === 0
                                                        ? "0px"
                                                        : idx === 1
                                                          ? `${compact ? 40 : 72}px`
                                                          : undefined,
                                                color: highlightColor,
                                                fontWeight: highlightColor ? 700 : undefined,
                                                width: cell.column.columnDef.size
                                                    ? `${cell.column.columnDef.size}px`
                                                    : undefined,
                                                minWidth: cell.column.columnDef.size
                                                    ? `${cell.column.columnDef.size}px`
                                                    : undefined,
                                            }}
                                        >
                                            {idx === 1 && !isAverageRow ? (
                                                <div className="flex items-center gap-1.5 group">
                                                    <span className="font-bold text-primary hover:underline cursor-pointer min-w-0 overflow-hidden">
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </span>
                                                    <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-primary cursor-pointer" />
                                                </div>
                                            ) : (
                                                flexRender(cell.column.columnDef.cell, cell.getContext())
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
}
